import { useState } from "react";
import { Loader2, Lock, Eye, EyeOff, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

function validatePolicy(pwd) {
  if (!pwd || pwd.length < 8) return "Password must be at least 8 characters.";
  if (!/[a-zA-Z]/.test(pwd)) return "Password must contain at least one letter.";
  if (!/[0-9]/.test(pwd)) return "Password must contain at least one number.";
  return null;
}

const Field = ({ label, value, onChange, show, testId }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-ink/80">{label}</label>
    <div className="relative">
      <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink/35" />
      <input
        data-testid={testId}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="••••••••"
        className="h-11 w-full rounded-xl border border-sky bg-white pl-11 pr-3.5 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ink/35 focus:border-loop focus:ring-4 focus:ring-loop/15"
      />
    </div>
  </div>
);

export function ChangePasswordForm({ forced = false, onSuccess, submitLabel = "Update password" }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const rules = [
    { label: "At least 8 characters", ok: next.length >= 8 },
    { label: "Contains a letter", ok: /[a-zA-Z]/.test(next) },
    { label: "Contains a number", ok: /[0-9]/.test(next) },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!forced && !current) return setError("Enter your current password.");
    const v = validatePolicy(next);
    if (v) return setError(v);
    if (next !== confirm) return setError("New passwords do not match.");
    setSaving(true);
    try {
      const payload = { new_password: next };
      if (!forced) payload.current_password = current;
      await api.post("/auth/change-password", payload);
      toast.success("Password updated successfully");
      setCurrent(""); setNext(""); setConfirm("");
      onSuccess?.();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      {!forced && (
        <Field label="Current password" value={current} onChange={setCurrent} show={show} testId="current-password-input" />
      )}
      <Field label="New password" value={next} onChange={setNext} show={show} testId="new-password-input" />
      <Field label="Confirm new password" value={confirm} onChange={setConfirm} show={show} testId="confirm-password-input" />

      <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password-visibility"
        className="flex items-center gap-1.5 text-xs font-medium text-loop hover:text-loophover">
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}{show ? "Hide" : "Show"} passwords
      </button>

      <ul className="space-y-1">
        {rules.map((r) => (
          <li key={r.label} className={`flex items-center gap-2 text-xs ${r.ok ? "text-emerald-600" : "text-ink/45"}`}>
            <Check className={`h-3.5 w-3.5 ${r.ok ? "opacity-100" : "opacity-30"}`} />{r.label}
          </li>
        ))}
      </ul>

      {error && (
        <div data-testid="change-password-error" className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
      )}

      <button type="submit" disabled={saving} data-testid="change-password-submit"
        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-loop text-sm font-semibold text-white shadow-lg shadow-loop/30 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-loophover disabled:translate-y-0 disabled:opacity-60">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : submitLabel}
      </button>
    </form>
  );
}
